import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import LanguageSelector from "@/components/LanguageSelector";
import { format } from "date-fns";
import {
  BarChart3, FileSpreadsheet, LogOut, User, Database, ChevronLeft, ChevronRight, 
  Filter, Key, Lock, Mail, CalendarIcon, Clock, Home, Settings, ArrowUp, ArrowDown, ArrowUpDown, Download
} from "lucide-react";
import "@/styles/fiori.css";

const COLORS = ['#0a6ed1', '#107e3e', '#e9730c', '#bb0000', '#6a6d70', '#354a5f', '#0854a0', '#1a9898'];

const UserDashboard = () => {
  const { user, token, logout } = useAuth();
  const { t, getMonths } = useLanguage();
  const navigate = useNavigate();
  const [salesData, setSalesData] = useState([]);
  const [stats, setStats] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Sorting
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Filters
  const [filterMode, setFilterMode] = useState("month");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  // Modals
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [profileEmail, setProfileEmail] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const months = getMonths();

  const initializeFilters = useCallback(async () => {
    try {
      const [yearsRes, statsRes] = await Promise.all([
        axios.get(`${API}/sales/years`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/sales/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const years = yearsRes.data.years || [];
      setAvailableYears(years);
      
      const latestYear = yearsRes.data.latest_year;
      const latestMonth = yearsRes.data.latest_month;
      if (latestYear) {
        setSelectedYear(latestYear.toString());
        setSelectedMonth(latestMonth ? latestMonth.toString() : "1");
      } else if (years.length > 0) {
        setSelectedYear(years[0].toString());
        setSelectedMonth("1");
      } else {
        const now = new Date();
        setSelectedYear(now.getFullYear().toString());
        setSelectedMonth((now.getMonth() + 1).toString());
      }
      setFiltersInitialized(true);
    } catch (error) {
      const now = new Date();
      setSelectedYear(now.getFullYear().toString());
      setSelectedMonth((now.getMonth() + 1).toString());
      setFiltersInitialized(true);
    } finally {
      setInitialLoading(false);
    }
  }, [token]);

  // Export function with authentication
  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      params.append('format', format);
      if (filterMode === 'year' && selectedYear) params.append('year', selectedYear);
      if (filterMode === 'month' && selectedYear && selectedMonth) {
        params.append('year', selectedYear);
        params.append('month', selectedMonth);
      }
      if (filterMode === 'custom' && startDate) params.append('start_date', startDate.toISOString());
      if (filterMode === 'custom' && endDate) params.append('end_date', endDate.toISOString());
      
      const response = await axios.get(`${API}/sales/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'xlsx' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          : 'text/csv' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `export_${user?.id?.substring(0, 8) || 'data'}.${format === 'xlsx' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t('exportData') + ' ' + format.toUpperCase());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Export failed');
    }
  };

  const fetchData = useCallback(async () => {
    if (!filtersInitialized) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '0'); // 0 means fetch all records without pagination
      const statsParams = new URLSearchParams();
      
      if (filterMode === "month" && selectedYear && selectedMonth) {
        params.append('year', selectedYear);
        params.append('month', selectedMonth);
        statsParams.append('year', selectedYear);
        statsParams.append('month', selectedMonth);
      } else if (filterMode === "year" && selectedYear) {
        params.append('year', selectedYear);
        statsParams.append('year', selectedYear);
      } else if (filterMode === "custom") {
        if (startDate) { params.append('start_date', startDate.toISOString()); statsParams.append('start_date', startDate.toISOString()); }
        if (endDate) { params.append('end_date', endDate.toISOString()); statsParams.append('end_date', endDate.toISOString()); }
      }

      const [dataRes, statsRes] = await Promise.all([
        axios.get(`${API}/sales?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/sales/stats?${statsParams.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setSalesData(dataRes.data.data || []);
      setTotal(dataRes.data.total || 0);
      setStats(statsRes.data);
    } catch (error) {
      toast.error(t('errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [token, filterMode, selectedYear, selectedMonth, startDate, endDate, t, filtersInitialized]);

  useEffect(() => { initializeFilters(); }, [initializeFilters]);
  useEffect(() => { fetchData(); }, [fetchData]);

  // Sorting function
  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const sortedData = [...salesData].sort((a, b) => {
    if (!sortColumn) return 0;
    const aVal = a[sortColumn] || '';
    const bVal = b[sortColumn] || '';
    
    if (sortDirection === 'asc') {
      return aVal.toString().localeCompare(bVal.toString());
    } else {
      return bVal.toString().localeCompare(aVal.toString());
    }
  });

  const handleLogout = () => { logout(); navigate("/login"); toast.success(t('sessionClosed')); };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) { toast.error("Complete all fields"); return; }
    if (newPassword !== confirmPassword) { toast.error("Passwords don't match"); return; }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('passwordUpdated'));
      setChangePasswordModalOpen(false);
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally { setChangingPassword(false); }
  };

  const openProfileModal = () => {
    setProfileName(user?.name || "");
    setProfileEmail(user?.email || "");
    setProfileModalOpen(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      // Users can only update their email
      await axios.put(`${API}/users/${user.id}`, { email: profileEmail }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('profileUpdated'));
      setProfileModalOpen(false);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorUpdating'));
    } finally { setSavingProfile(false); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try { return new Date(dateStr).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } 
    catch { return dateStr; }
  };

  const columns = [
    { key: 'ref', label: 'Ref' },
    { key: 'fulfillment', label: 'Fulfillment' },
    { key: 'operatorurl', label: 'OperatorURL' },
    { key: 'billingid', label: 'BillingID' },
    { key: 'transactiontime', label: 'Transaction Time', format: formatDate },
    { key: 'transactiontype', label: 'Transaction Type' },
    { key: 'user', label: 'User' },
    { key: 'resource', label: 'Resource' },
    { key: 'oldperm', label: 'Old Perm' },
    { key: 'newperm', label: 'New Perm' },
    { key: 'resourcetype', label: 'Resource Type' },
    { key: 'devicetype', label: 'Device Type' },
    { key: 'softwareversion', label: 'Software Version' },
    { key: 'distid', label: 'Dist ID' },
    { key: 'clientsoftwareversion', label: 'Client SW Version' },
    { key: 'clientversion', label: 'Client Version' },
    { key: 'clientos', label: 'Client OS' },
  ];

  if (initialLoading) {
    return (
      <div className="fiori-login-page">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[var(--sapBrandColor)] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {/* SAP Shell Header */}
      <header className="sap-shell-header">
        <div className="sap-shell-logo">
          <div className="sap-shell-logo-icon"><BarChart3 size={18} /></div>
          <span className="sap-shell-title">{t('appName')}</span>
        </div>
        <div className="sap-shell-actions">
          <LanguageSelector />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="sap-shell-button" onClick={openProfileModal}>
                  <div className="sap-shell-avatar">{user?.username?.charAt(0).toUpperCase()}</div>
                  <span>{user?.username}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent><p>{t('profileTooltip')}</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="sap-shell-button" onClick={handleLogout}>
                  <LogOut size={16} />
                </button>
              </TooltipTrigger>
              <TooltipContent><p>{t('logoutTooltip')}</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </header>

      <div className="fiori-layout">
        {/* Sidebar */}
        <aside className="fiori-sidebar">
          <div className="fiori-sidebar-section">
            <div className="fiori-sidebar-title">{t('navigation')}</div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="fiori-nav-item active">
                    <Home size={18} className="fiori-nav-icon" />
                    <span>{t('myDashboard')}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right"><p>{t('dashboardTooltip')}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="fiori-sidebar-section">
            <div className="fiori-sidebar-title">{t('settings')}</div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="fiori-nav-item" onClick={() => setChangePasswordModalOpen(true)}>
                    <Key size={18} className="fiori-nav-icon" />
                    <span>{t('changePassword')}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right"><p>{t('changePasswordTooltip')}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="fiori-nav-item" onClick={openProfileModal}>
                    <Settings size={18} className="fiori-nav-icon" />
                    <span>{t('myProfile')}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right"><p>{t('profileTooltip')}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="fiori-nav-item" onClick={handleLogout} style={{ color: 'var(--sapErrorColor)' }}>
                    <LogOut size={18} className="fiori-nav-icon" style={{ color: 'var(--sapErrorColor)' }} />
                    <span>{t('logout')}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right"><p>{t('logoutTooltip')}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </aside>

        {/* Main Content */}
        <main className="fiori-main">
          {/* Page Header */}
          <div className="fiori-page-header">
            <h1 className="fiori-page-title">{t('myDashboard')}</h1>
            <p className="fiori-page-subtitle">{user?.name || user?.username} • {user?.email || ''}</p>
          </div>

          {/* Filter Toolbar */}
          <div className="fiori-toolbar">
            <Filter size={16} style={{ color: 'var(--sapContent_LabelColor)' }} />
            <select className="fiori-select" value={filterMode} onChange={(e) => setFilterMode(e.target.value)}>
              <option value="month">{t('month')}</option>
              <option value="year">{t('year')}</option>
              <option value="all">{t('allTime')}</option>
              <option value="custom">{t('customRange')}</option>
            </select>

            {(filterMode === "month" || filterMode === "year") && (
              <select className="fiori-select" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
                {availableYears.map((y) => (<option key={y} value={y}>{y}</option>))}
              </select>
            )}

            {filterMode === "month" && (
              <select className="fiori-select" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                {months.map((m) => (<option key={m.value} value={m.value}>{m.label}</option>))}
              </select>
            )}

            {filterMode === "custom" && (
              <>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="fiori-button ghost">
                      <CalendarIcon size={14} />
                      {startDate ? format(startDate, "dd/MM/yyyy") : t('from')}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={startDate} onSelect={setStartDate} /></PopoverContent>
                </Popover>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="fiori-button ghost">
                      <CalendarIcon size={14} />
                      {endDate ? format(endDate, "dd/MM/yyyy") : t('to')}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={endDate} onSelect={setEndDate} /></PopoverContent>
                </Popover>
              </>
            )}

            <div className="fiori-toolbar-spacer" />
            
            <div className="fiori-info-label">
              {t('totalRecords')}: <span className="fiori-info-value">{stats?.total_all_records || 0}</span>
            </div>
            <div className="fiori-toolbar-separator" />
            <div className="fiori-info-label">
              {t('selectedRecords')}: <span className="fiori-info-value" style={{ color: 'var(--sapBrandColor)' }}>{stats?.total_selected || 0}</span>
            </div>
            <div className="fiori-toolbar-separator" />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                className="fiori-button ghost"
                onClick={() => handleExport('csv')}
              >
                <Download size={14} /> CSV
              </button>
              <button 
                className="fiori-button ghost"
                onClick={() => handleExport('xlsx')}
              >
                <Download size={14} /> Excel
              </button>
            </div>
          </div>

          {loading ? (
            <div className="fiori-empty-state">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-[var(--sapBrandColor)] border-t-transparent mx-auto"></div>
            </div>
          ) : (
            <>
              {/* Stats Tiles */}
              <div className="fiori-tiles-grid">
                <div className="fiori-tile">
                  <div className="fiori-tile-header">
                    <div className="fiori-tile-icon brand"><Database size={20} /></div>
                  </div>
                  <div className="fiori-tile-value">{stats?.total_all_records || 0}</div>
                  <div className="fiori-tile-label">{t('totalRecords')}</div>
                </div>
                <div className="fiori-tile">
                  <div className="fiori-tile-header">
                    <div className="fiori-tile-icon success"><Filter size={20} /></div>
                  </div>
                  <div className="fiori-tile-value">{stats?.total_selected || 0}</div>
                  <div className="fiori-tile-label">{t('selectedRecords')}</div>
                </div>
                <div className="fiori-tile">
                  <div className="fiori-tile-header">
                    <div className="fiori-tile-icon warning"><BarChart3 size={20} /></div>
                  </div>
                  <div className="fiori-tile-value">{stats?.client_versions?.length || 0}</div>
                  <div className="fiori-tile-label">{t('clientVersions')}</div>
                </div>
                <div className="fiori-tile">
                  <div className="fiori-tile-header">
                    <div className="fiori-tile-icon neutral"><Clock size={20} /></div>
                  </div>
                  <div className="fiori-tile-value">{stats?.last_import_count || 0}</div>
                  <div className="fiori-tile-label">{t('lastImport')}</div>
                </div>
              </div>

              {/* Charts */}
              <div className="fiori-charts-grid">
                <div className="fiori-card">
                  <div className="fiori-card-header">
                    <h3 className="fiori-card-title">{t('transactionsByMonth')}</h3>
                  </div>
                  <div className="fiori-card-content">
                    {stats?.monthly_data?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={stats.monthly_data}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e4" />
                          <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6a6d70' }} stroke="#e4e4e4" />
                          <YAxis tick={{ fontSize: 11, fill: '#6a6d70' }} stroke="#e4e4e4" />
                          <RechartsTooltip contentStyle={{ borderRadius: '4px', border: '1px solid #e4e4e4', fontFamily: 'var(--sapFontFamily)' }} />
                          <Line type="monotone" dataKey="count" stroke="#0a6ed1" strokeWidth={2} dot={{ fill: '#0a6ed1', r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="fiori-empty-state"><p>{t('noDataAvailable')}</p></div>
                    )}
                  </div>
                </div>

                <div className="fiori-card">
                  <div className="fiori-card-header">
                    <h3 className="fiori-card-title">{t('clientOSDistribution')}</h3>
                  </div>
                  <div className="fiori-card-content">
                    {stats?.client_os?.length > 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <ResponsiveContainer width="50%" height={200}>
                          <PieChart>
                            <Pie data={stats.client_os.slice(0, 8)} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={75}>
                              {stats.client_os.slice(0, 8).map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                            </Pie>
                            <RechartsTooltip />
                          </PieChart>
                        </ResponsiveContainer>
                        <div style={{ flex: 1, fontSize: '0.8125rem' }}>
                          {stats.client_os.slice(0, 6).map((item, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                              <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: COLORS[i % COLORS.length] }} />
                              <span style={{ flex: 1, color: 'var(--sapTextColor)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                              <span style={{ fontWeight: 600 }}>{item.percentage}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="fiori-empty-state"><p>{t('noDataAvailable')}</p></div>
                    )}
                  </div>
                </div>
              </div>

              {/* Client Versions Chart */}
              <div className="fiori-card" style={{ marginBottom: '1rem' }}>
                <div className="fiori-card-header">
                  <h3 className="fiori-card-title">{t('clientVersions')}</h3>
                </div>
                <div className="fiori-card-content">
                  {stats?.client_versions?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={stats.client_versions.slice(0, 8)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e4" />
                        <XAxis type="number" tick={{ fontSize: 11, fill: '#6a6d70' }} stroke="#e4e4e4" />
                        <YAxis dataKey="version" type="category" width={180} tick={{ fontSize: 10, fill: '#6a6d70' }} stroke="#e4e4e4" />
                        <RechartsTooltip contentStyle={{ borderRadius: '4px', border: '1px solid #e4e4e4' }} />
                        <Bar dataKey="count" fill="#0a6ed1" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="fiori-empty-state"><p>{t('noDataAvailable')}</p></div>
                  )}
                </div>
              </div>

              {/* Data Table */}
              <div className="fiori-table-container">
                <div className="fiori-table-header">
                  <div>
                    <span className="fiori-table-title">{t('dataTable')}</span>
                    <span className="fiori-table-count">({total} {t('records')})</span>
                  </div>
                  {sortColumn && (
                    <button className="fiori-button ghost" onClick={() => { setSortColumn(null); setSortDirection('asc'); }}>
                      {t('clearSort')}
                    </button>
                  )}
                </div>
                {salesData.length === 0 ? (
                  <div className="fiori-empty-state">
                    <FileSpreadsheet size={48} className="fiori-empty-state-icon" />
                    <p className="fiori-empty-state-title">{t('noData')}</p>
                    <p className="fiori-empty-state-text">{filterMode !== "all" ? t('noDataForFilters') : t('contactAdminForData')}</p>
                  </div>
                ) : (
                  <>
                    <div className="fiori-table-scroll" style={{ maxHeight: '600px' }}>
                      <table className="fiori-table">
                        <thead>
                          <tr>
                            <th style={{ width: '50px', textAlign: 'center' }}>#</th>
                            {columns.map((col) => (
                              <th 
                                key={col.key} 
                                onClick={() => handleSort(col.key)}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  {col.label}
                                  {sortColumn === col.key ? (
                                    sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                                  ) : (
                                    <ArrowUpDown size={14} style={{ opacity: 0.3 }} />
                                  )}
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sortedData.map((record, idx) => (
                            <tr key={record.id || idx}>
                              <td style={{ textAlign: 'center', color: 'var(--sapContent_LabelColor)', fontWeight: 500 }}>{idx + 1}</td>
                              {columns.map((col) => (
                                <td key={col.key} title={record[col.key] || '-'}>
                                  {col.format ? col.format(record[col.key]) : (record[col.key] || '-')}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="fiori-table-footer">
                      <span style={{ fontSize: '0.8125rem', color: 'var(--sapContent_LabelColor)' }}>
                        {t('showingAllRecords', { count: total })}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {/* Change Password Modal */}
      <Dialog open={changePasswordModalOpen} onOpenChange={setChangePasswordModalOpen}>
        <DialogContent className="fiori-dialog sm:max-w-md">
          <DialogHeader className="fiori-dialog-header">
            <DialogTitle className="fiori-dialog-title">{t('changePasswordTitle')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="fiori-dialog-content">
            <div className="fiori-form-group">
              <label className="fiori-form-label">{t('currentPassword')}</label>
              <input type="password" className="fiori-form-input" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            </div>
            <div className="fiori-form-group">
              <label className="fiori-form-label">{t('newPassword')}</label>
              <input type="password" className="fiori-form-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            <div className="fiori-form-group">
              <label className="fiori-form-label">{t('confirmPassword')}</label>
              <input type="password" className="fiori-form-input" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            </div>
            <div className="fiori-dialog-footer" style={{ padding: 0, border: 'none', marginTop: '1rem' }}>
              <button type="button" className="fiori-button" onClick={() => setChangePasswordModalOpen(false)}>{t('cancel')}</button>
              <button type="submit" className="fiori-button emphasized" disabled={changingPassword}>
                {changingPassword ? t('changing') : t('changePassword')}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Profile Modal - Read Only */}
      <Dialog open={profileModalOpen} onOpenChange={setProfileModalOpen}>
        <DialogContent className="fiori-dialog sm:max-w-md">
          <DialogHeader className="fiori-dialog-header">
            <DialogTitle className="fiori-dialog-title">{t('myProfile')}</DialogTitle>
            <DialogDescription style={{ color: 'var(--sapContent_LabelColor)', fontSize: '0.875rem' }}>{t('viewProfileInfo')}</DialogDescription>
          </DialogHeader>
          <div className="fiori-dialog-content">
            <div className="fiori-form-group">
              <label className="fiori-form-label">{t('username')}</label>
              <input className="fiori-form-input" value={user?.username || "-"} disabled style={{ backgroundColor: 'var(--sapBackgroundColor)' }} />
            </div>
            <div className="fiori-form-group">
              <label className="fiori-form-label">{t('clientName')}</label>
              <input className="fiori-form-input" value={user?.name || "-"} disabled style={{ backgroundColor: 'var(--sapBackgroundColor)' }} />
            </div>
            <div className="fiori-form-group">
              <label className="fiori-form-label">{t('contactEmail')}</label>
              <input className="fiori-form-input" value={user?.email || "-"} disabled style={{ backgroundColor: 'var(--sapBackgroundColor)' }} />
            </div>
            <div className="fiori-form-group">
              <label className="fiori-form-label">{t('operatorUrl')}</label>
              <input className="fiori-form-input" value={user?.operator_url || "-"} disabled style={{ backgroundColor: 'var(--sapBackgroundColor)' }} />
            </div>
            <div className="fiori-dialog-footer" style={{ padding: 0, border: 'none', marginTop: '1rem' }}>
              <button type="button" className="fiori-button emphasized" onClick={() => setProfileModalOpen(false)}>{t('close')}</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserDashboard;
